import { useMemo, useState, useCallback } from 'react';
import { i18n } from '@investigator/shared/localization';
import { ROUTES } from '@investigator/shared/constants';
import { Box, VStack, Text, Link, HStack } from '@chakra-ui/react';
import { EntityComment } from '../../queries/types';
import { validateClickTarget } from '../../utils/validateClickTarget';
import {
  SeverityBucketLookup,
  SeverityColorMap,
} from '@investigator/shared/utils';

const FORMATTED_DATE_CONFIG = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
} as const;

interface NoteItemProps {
  entityName: string;
  note: EntityComment;
  start?: number;
  end?: number;
}

export const NoteItem = ({
  entityName,
  note,
  start,
  end,
}: NoteItemProps): JSX.Element => {
  const notesPopoverLocales = i18n.pages.entities.notesPopover;
  const [isExpanded, setIsExpanded] = useState(false);
  const formattedDate = useMemo(() => {
    return new Date(note.created_at * 1000).toLocaleDateString(
      'en-US',
      FORMATTED_DATE_CONFIG,
    );
  }, [note.created_at]);

  const hasMoreContent = useMemo(() => {
    return note.comment.split('\n').length > 2 || note.comment.length > 150;
  }, [note.comment]);

  // Note: IP address is not available directly on Comment type
  // If needed, would need to fetch from detection via parent.parent_object_id
  const hasDetectionInfo = note.severity || note.alert_name || '';
  const detectionInfoText = note.alert_name || '';

  const severityColor = useMemo(() => {
    return note.severity !== undefined && note.severity !== null
      ? SeverityColorMap[SeverityBucketLookup[note.severity]]
      : null;
  }, [note.severity]);

  // TODO - is this needed? won't all notes be associated with a detection?
  // Check if this note is associated with a detection
  const detectionId = useMemo(() => {
    return note.parent?.parent_object_type === 'detection'
      ? note.parent.parent_object_id
      : null;
  }, [note.parent]);

  // Build detection details URL with date range query params
  const detectionUrl = useMemo(() => {
    if (!detectionId) return null;
    const queryString = start && end ? `?start=${start}&end=${end}` : '';
    return ROUTES.detectionDetails(detectionId, queryString);
  }, [detectionId, start, end]);

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't navigate if clicking on interactive elements
      const target = e.target as HTMLElement;

      const shouldPreventNavigation = validateClickTarget(
        target,
        ['A'], // Check if target is an anchor tag
        ['a', 'button'], // Check if target is inside an anchor or button
        [notesPopoverLocales.viewMore, notesPopoverLocales.viewLess], // Check text content
      );

      if (shouldPreventNavigation) {
        return;
      }

      if (detectionUrl) {
        // Per Mejji, this will open a new tab:
        window.open(detectionUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [detectionUrl, notesPopoverLocales.viewMore, notesPopoverLocales.viewLess],
  );

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <Box
      data-testid={`note-card-${note.comment_id}`}
      bg="layer.2"
      borderRadius="base"
      p={2}
      borderBottom="1px solid"
      borderColor="border.layer.2"
      _last={{ borderBottom: 'none' }}
      onClick={detectionUrl ? handleCardClick : undefined}
      cursor={detectionUrl ? 'pointer' : 'default'}
      _hover={
        detectionUrl
          ? {
              bg: 'layer.3',
            }
          : {}
      }
      transition="background-color 0.2s"
    >
      <VStack
        align="stretch"
        spacing={2}
        fontSize="14px"
        lineHeight="20px"
        letterSpacing="0.16px"
      >
        <Box
          display="flex"
          alignItems="flex-start"
          flexWrap="nowrap"
          width="100%"
          minWidth={0}
        >
          <Text as="span" color="text.primary" flexShrink={0}>
            "
          </Text>
          <Text
            data-testid={`comment-text-${note.comment_id}`}
            color="text.primary"
            wordBreak="break-word"
            overflowWrap="break-word"
            whiteSpace={isExpanded ? 'pre-wrap' : 'normal'}
            noOfLines={isExpanded ? undefined : 2}
            flex="1"
            minWidth={0}
            width="100%"
            fontWeight={400}
          >
            {note.comment}
            {/* To prevent missing end quote when truncated for comments with multiple lines */}
            {(!hasMoreContent || isExpanded) && '"'}
          </Text>
          {/* To prevent missing end quote when truncated for comments with multiple lines */}
          {hasMoreContent && !isExpanded && (
            <Text
              as="span"
              color="text.primary"
              flexShrink={0}
              ml="2px"
              alignSelf="flex-end"
              height="100%"
            >
              "
            </Text>
          )}
        </Box>
        {hasMoreContent && (
          <Link
            aria-label={
              isExpanded
                ? notesPopoverLocales.viewLess
                : notesPopoverLocales.viewMore
            }
            data-testid={`view-more-link-${note.comment_id}`}
            color="text.link"
            onClick={handleToggleExpanded}
            cursor="pointer"
          >
            {isExpanded
              ? notesPopoverLocales.viewLess
              : notesPopoverLocales.viewMore}
          </Link>
        )}

        {hasDetectionInfo && (
          <HStack
            spacing={2}
            alignItems="center"
            flex="1"
            minWidth={0}
            width="100%"
          >
            {note.severity !== undefined &&
              note.severity !== null &&
              severityColor && (
                <Text
                  fontWeight="semibold"
                  color={`${severityColor}.500`}
                  flexShrink={0}
                >
                  {note.severity}
                </Text>
              )}
            {detectionInfoText && (
              <Text
                color="text.secondary"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                minWidth={0}
                maxWidth="calc(100% - 60px)"
              >
                {detectionInfoText}
              </Text>
            )}
            <Text color="text.secondary" flexShrink={0}>
              |
            </Text>
            {entityName && (
              <Text
                color="text.secondary"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {entityName}
              </Text>
            )}
          </HStack>
        )}

        <Text color="text.secondary">
          {`${note.author_alias} • ${formattedDate}`}
        </Text>
      </VStack>
    </Box>
  );
};
